import { Component, Output, EventEmitter, input } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { ImageDialogComponent } from "../image-dialog/image-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { getDictionaryItem } from "../../utils/dictionary.domain";
import { Annotation } from "../../interfaces/annotation.interface";

@Component({
    selector: "app-image-gallery",
    standalone: true,
    templateUrl: "./image-gallery.component.html",
    styleUrls: ["./image-gallery.component.scss"],
    imports: [MatIcon]
})
export class ImageGalleryComponent {
    @Output() imageGallery = new EventEmitter<{ title: string; url: string }[]>();
    @Output() newAnnotations = new EventEmitter<{ title: string; url: string }[]>();
    @Output() removeAnnotations = new EventEmitter<Annotation[]>();

    readonly caseImages = input<Annotation[]>([]);
    readonly isDisabled = input<boolean>(false);
    readonly isCaseDetail = input<boolean>(false);
    readonly isEditing = input<boolean>(false);

    imageGalleryId: string = "image-gallery";
    imageGalleryInput: string = "image-gallery-input";
    imageGalleryImage: string = "image-gallery-file";
    imageGalleryImageList: string = "image-gallery-image-list";
    imageGalleryRemoveButton: string = "image-gallery-remove-button";
    imageGalleryDragOverlay: string = "image-gallery-drag-overlay";

    selectedFiles?: FileList;
    activeImage: string = "";
    selectedFilesMessage: string = "Ingen filter tilføjet.";
    previewFiles: { title: string; url: string }[] = [];
    public isDragOver = false;
    private dragLeaveTimeout: any;

    casefileImages: Annotation[] = [];
    removeFilesList: Annotation[] = [];
    newFilesList?: FileList;
    newAnnotation?: { title: string; url: string }[] = [];

    constructor(private dialog: MatDialog) {
        this.casefileImages = this.caseImages();
    }

    public getDictionaryItem = getDictionaryItem;

    ngOnInit() {
        if (this.caseImages()) {
            this.casefileImages = this.caseImages();
        }
        this.useEventListeners();
    }

    ngOnDestroy() {
        this.useEventListeners();
    }

    public selectFiles(event: Event): void {
        this.selectedFiles = (event?.target as HTMLInputElement)?.files || undefined;
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFilesMessage = `${input.files.length} fil(er) tilføjet.`;
        } else {
            this.selectedFilesMessage = "Ingen filer tilføjet.";
        }

        if (this.selectedFiles && this.selectedFiles[0]) {
            const numberOfFiles = this.selectedFiles.length;
            for (let i = 0; i < numberOfFiles; i++) {
                const reader = new FileReader();

                reader.onload = (e: any) => {
                    const fileName = this.selectedFiles?.length ? this.selectedFiles[i]?.name.split(".").slice(0, -1).join(".") : `vedhæftet-fil-${i + 1}`;
                    this.previewFiles.push({ title: fileName, url: e.target.result });
                    this.imageGallery.emit(this.previewFiles);
                };

                reader.readAsDataURL(this.selectedFiles[i]);
            }
        }
    }

    public addNewFiles(event: Event): void {
        this.newFilesList = (event?.target as HTMLInputElement)?.files || undefined;

        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFilesMessage = `${input.files.length} fil(er) tilføjet.`;
        } else {
            this.selectedFilesMessage = "Ingen filer tilføjet.";
        }

        if (this.newFilesList && this.newFilesList[0]) {
            const numberOfFiles = this.newFilesList.length;
            for (let i = 0; i < numberOfFiles; i++) {
                const reader = new FileReader();

                reader.onload = (e: any) => {
                    const fileName = this.newFilesList?.length ? this.newFilesList[i]?.name.split(".").slice(0, -1).join(".") : `vedhæftet-fil-${i + 1}`;
                    const result = e.target.result as string;
                    const base64Index = result.indexOf("base64,") + "base64,".length;
                    const documentBody = result.substring(base64Index);
                    const mediatype = result.substring(5, result.indexOf(";"));

                    if (this.casefileImages) {
                        this.casefileImages.push({ filename: fileName, documentBody: documentBody, mediatype: mediatype, objectId: "", annotationid: "" });
                        this.newAnnotation?.push({ title: fileName, url: result });
                        this.newAnnotations.emit(this.newAnnotation);
                    }
                };

                reader.readAsDataURL(this.newFilesList[i]);
            }
        }
    }

    public removeCaseFile(file: Annotation) {
        if (file.objectId === "") {
            this.casefileImages = this.casefileImages.filter((f) => f !== file);
        } else if (this.removeFilesList.includes(file)) {
            this.removeFilesList = this.removeFilesList.filter((f) => f !== file);
            this.removeAnnotations.emit(this.removeFilesList);
        } else {
            this.removeFilesList.push(file);
            this.removeAnnotations.emit(this.removeFilesList);
        }
    }

    public removeFile(index: number) {
        this.previewFiles.splice(index, 1);
        this.imageGallery.emit(this.previewFiles);
    }

    public windowDragOver = (event: DragEvent) => {
        event.preventDefault();
        clearTimeout(this.dragLeaveTimeout);
        if (!this.isDragOver) {
            this.isDragOver = true;
        }
    };

    public windowDragLeave = (event: DragEvent) => {
        event.preventDefault();
        if ((event as any).relatedTarget == null && event.clientX === 0 && event.clientY === 0) {
            this.dragLeaveTimeout = setTimeout(() => {
                this.isDragOver = false;
            }, 100);
        }
    };

    public windowDrop = (event: DragEvent) => {
        event.preventDefault();
        this.isDragOver = false;
        if (event.dataTransfer && event.dataTransfer.files) {
            const files = {
                target: {
                    files: event.dataTransfer.files
                }
            } as unknown as Event;

            if (this.isEditing() && this.isCaseDetail()) {
                this.addNewFiles(files);
            } else {
                this.selectFiles(files);
            }
        }
    };

    public useEventListeners() {
        window.addEventListener("dragover", this.windowDragOver, false);
        window.addEventListener("dragleave", this.windowDragLeave, false);
        window.addEventListener("drop", this.windowDrop, false);
    }

    public getFileDataString(annotation: Annotation): string {
        if (!annotation) return "";

        const annotationString = `data:${annotation.mediatype};base64,${annotation.documentBody}`;
        return annotationString;
    }

    public getAnnotationFile(file: string): string {
        return file;
    }

    public showDocument(annotation: Annotation) {
        const annotationString = `data:${annotation.mediatype};base64,${annotation.documentBody}`;
        const html =
            "<html>" +
            "<style>html, body { padding: 0; margin: 0; } iframe { width: 100%; height: 100%; border: 0;}  </style>" +
            "<body>" +
            '<iframe type="application/pdf" src="' +
            annotationString +
            '"></iframe>' +
            "</body></html>";
        const a = window.open("about:blank", "Zupfnoter");
        if (a) {
            a.document.write(html);
            a.document.close();
        }
    }

    public getIcon(src: string) {
        if (src.startsWith("data:")) {
            let result = src.match(/application[^;]*/);
            if (result) {
                src = result[0];
            }
        }
        const icons: { [key: string]: string } = {
            "application/pdf": "picture_as_pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "document_scanner",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "table_chart",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": "present_to_all"
        };

        return icons[src] ?? ""; //"attach_file";
    }

    public openImageDialog(src: string) {
        const imageDialogRef = this.dialog.open(ImageDialogComponent, {
            data: { activeImageSrc: src ?? "" },
            width: "90%",
            maxWidth: "992px",
            maxHeight: "90vh",
            height: "fit-content"
        });
        return imageDialogRef;
    }
}
